import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/contexts/app-context";
import { createChapter, updateChapterTitle } from "@/services/supabase/chapters";
import { ChapterToolOutput } from "@/types/ai-schemas";
import { cn } from "@/lib/utils";
import { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { BookOpen, Check, ChevronDown, ChevronUp, History, Loader2, Play, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const tryParseJSON = (jsonString: string) => {
    try { return JSON.parse(jsonString); } catch { return null; }
};

export const CreateChapterTool: ToolCallMessagePartComponent = ({ argsText, result }) => {
    const { storyId, activeStoryId } = useApp();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [editableItems, setEditableItems] = useState<ChapterToolOutput[]>([]);
    const [hasUserEdited, setHasUserEdited] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const rawData = useMemo(() => typeof result === 'string' ? tryParseJSON(result) : result, [result]);
    const argsData = useMemo(() => typeof argsText === 'string' ? tryParseJSON(argsText) : argsText, [argsText]);
    const hasData = rawData || argsData;
    const isLoading = result === undefined && !hasData;

    useEffect(() => {
        if (hasUserEdited) return;
        const sourceData = rawData || argsData;
        if (!sourceData) return;

        // Unwrap tool result wrapper: {success, message, data: {...}} → data
        const unwrapped = (sourceData && typeof sourceData === 'object' && 'data' in sourceData && sourceData.data && typeof sourceData.data === 'object')
            ? sourceData.data
            : sourceData;

        let parsedItems: any[] = [];
        if (Array.isArray(unwrapped)) parsedItems = unwrapped;
        else if (unwrapped.items && Array.isArray(unwrapped.items)) parsedItems = unwrapped.items;
        else parsedItems = [unwrapped];

        const items: ChapterToolOutput[] = parsedItems.map((item: any) => {
            if (item.chapterName) return item as ChapterToolOutput;
            if (item.query) {
                let id: number | undefined;
                let chapterName = item.query;
                let chapterOutline = "";
                if (item.additionalContext) {
                    const idMatch = item.additionalContext.match(/Chapter ID:\s*(\d+)/i);
                    if (idMatch?.[1]) id = parseInt(idMatch[1], 10);
                }
                const parts = item.query.split(/\s*-\s*/, 2);
                if (parts.length === 2) { chapterName = parts[0].trim(); chapterOutline = parts[1].trim(); }
                return { type: id ? 'update_chapter' : 'chapter', _action: id ? 'update' : 'create', id, chapterName, chapterOutline, display_order: item.display_order } as ChapterToolOutput;
            }
            return { type: 'chapter', _action: 'create', chapterName: 'New Chapter', chapterOutline: JSON.stringify(item) } as ChapterToolOutput;
        });
        setEditableItems(items);
    }, [rawData, argsData, hasUserEdited]);

    const hasItems = editableItems.length > 0;
    const isUpdate = editableItems.some(item => item._action === 'update' || item.type === 'update_chapter');

    const handleAction = async () => {
        const contextStoryId = storyId || activeStoryId;
        if (!contextStoryId) { toast.error("Story context not found."); return; }
        const sid = Number(contextStoryId);
        setIsProcessing(true);
        try {
            await Promise.all(editableItems.map(async (item) => {
                if (item._action === 'update' || item.type === 'update_chapter') {
                    if (!item.id) throw new Error("Missing ID for update");
                    return updateChapterTitle(item.id, item.chapterName, item.chapterOutline);
                }
                return createChapter(sid, item.chapterName, item.chapterOutline);
            }));
            toast.success(isUpdate ? "Chapter updated!" : "Chapter created!");
            setIsCollapsed(true);
        } catch (error) {
            console.error("ChapterAction: Error:", error);
            toast.error("Failed to process chapters.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleUpdateItem = (index: number, field: keyof ChapterToolOutput, value: string) => {
        setHasUserEdited(true);
        setEditableItems(prev => { const n = [...prev]; n[index] = { ...n[index], [field]: value }; return n; });
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="flex items-center gap-2.5 my-3 px-1 text-sm text-muted-foreground">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                    <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
                </div>
                <span>Planning chapter structure…</span>
            </div>
        );
    }

    const activeItem = editableItems[activeIndex];

    return (
        <div className="my-3 rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            {/* Header */}
            <div
                className="flex items-center gap-3 px-4 py-3.5 cursor-pointer select-none bg-muted/40 hover:bg-muted/60 transition-colors border-b border-border"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl",
                    isUpdate ? "bg-amber-100 dark:bg-amber-900/30 text-amber-600" : "bg-primary/10 text-primary"
                )}>
                    {isUpdate ? <History className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-foreground">
                            {isUpdate ? "Update Chapter" : "Create Chapter"}
                        </p>
                        {hasItems && (
                            <Badge variant="secondary" className="text-[10px] h-4 px-1.5 rounded-sm font-medium">
                                {editableItems.length}
                            </Badge>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{isUpdate ? "Review changes before applying" : "Review and add to story"}</p>
                </div>
                <div className="text-muted-foreground/60">
                    {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                </div>
            </div>

            {!isCollapsed && hasItems && (
                <>
                    {/* Tab strip if multiple chapters */}
                    {editableItems.length > 1 && (
                        <div className="flex overflow-x-auto border-b border-border bg-muted/20">
                            {editableItems.map((item, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveIndex(i)}
                                    className={cn(
                                        "flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors",
                                        i === activeIndex
                                            ? "border-primary text-foreground"
                                            : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30"
                                    )}
                                >
                                    <BookOpen className="h-3 w-3" />
                                    {item.chapterName?.slice(0, 24) || `Chapter ${i + 1}`}
                                </button>
                            ))}
                        </div>
                    )}

                    {activeItem && (
                        <div>
                            {/* Chapter title field */}
                            <div className="px-4 pt-4 pb-3">
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
                                        Chapter Title <span className="text-destructive">*</span>
                                    </label>
                                    <Badge
                                        variant={activeItem._action === 'update' || activeItem.type === 'update_chapter' ? "secondary" : "default"}
                                        className="text-[10px] h-4 px-1.5 uppercase tracking-wide shrink-0"
                                    >
                                        {activeItem._action || (activeItem.type === 'update_chapter' ? 'UPDATE' : 'CREATE')}
                                    </Badge>
                                </div>
                                <Input
                                    value={activeItem.chapterName || ''}
                                    onChange={(e) => handleUpdateItem(activeIndex, 'chapterName', e.target.value)}
                                    className="font-medium bg-background border-border/60 focus-visible:ring-primary/30"
                                    placeholder="Enter chapter title…"
                                />
                            </div>

                            {/* Divider */}
                            <div className="mx-4 border-t border-border/50" />

                            {/* Outline field */}
                            <div className="px-4 pt-3 pb-4">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 block mb-1.5">
                                    Outline / Description
                                </label>
                                <Textarea
                                    value={activeItem.chapterOutline || ''}
                                    onChange={(e) => handleUpdateItem(activeIndex, 'chapterOutline', e.target.value)}
                                    className="min-h-[120px] leading-relaxed bg-background border-border/60 text-sm resize-none focus-visible:ring-primary/30"
                                    placeholder="Enter chapter description or outline…"
                                />
                            </div>

                            {/* Update badge */}
                            {(activeItem._action === 'update' || activeItem.type === 'update_chapter') && (
                                <div className="px-4 pb-3">
                                    <div className="inline-flex items-center gap-1.5 text-[10px] text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 rounded-lg border border-amber-200/60 dark:border-amber-800/30">
                                        <History className="h-3 w-3" />
                                        Updating existing chapter #{activeItem.id}
                                    </div>
                                </div>
                            )}

                            {/* Tags */}
                            {activeItem.tags && activeItem.tags.length > 0 && (
                                <div className="px-4 pb-3 flex flex-wrap gap-1.5">
                                    {activeItem.tags.map((tag, i) => (
                                        <Badge key={i} variant="outline" className="text-[10px] font-normal">{tag}</Badge>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
                        <p className="text-[11px] text-muted-foreground/60">{editableItems.length} chapter{editableItems.length > 1 ? 's' : ''} ready</p>
                        <div className="flex gap-2">
                            <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-foreground" onClick={() => setIsCollapsed(true)}>
                                Dismiss
                            </Button>
                            <Button
                                size="sm"
                                className={cn("h-8", isUpdate ? "bg-amber-600 hover:bg-amber-700 text-white" : "")}
                                onClick={handleAction}
                                disabled={isProcessing}
                            >
                                {isProcessing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : isUpdate ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Play className="mr-1.5 h-3.5 w-3.5 fill-current" />}
                                {isUpdate ? "Apply Changes" : "Create Chapter"}
                            </Button>
                        </div>
                    </div>
                </>
            )}

            {!isCollapsed && !hasItems && (
                <div className="px-4 py-6 text-center text-muted-foreground text-sm">
                    <p>No valid chapter data found.</p>
                    <pre className="mt-2 text-xs text-left bg-muted p-2 rounded-lg overflow-auto max-h-32 opacity-50">{argsText}</pre>
                </div>
            )}
        </div>
    );
};
